import { ApplicationController } from '@aha-app/mvc';
import askFredAnything, {
  type AskFredAnythingArgs,
} from 'components/Fred/askFredAnything';
import { DeleteRecordAction } from 'components/SelectionController/models/commonActions/DeleteRecordAction';
import RoutingBehaviors, {
  type IRoutingBehaviors,
  type PathBuilder,
} from 'components/framework/RoutingBehaviors';
import {
  HasReactiveRailsBehaviors,
  type IHasReactiveRailsBehaviors,
} from 'components/framework/controllers/concerns/HasReactiveRailsBehaviors';
import HasSelection, {
  type IHasSelection,
} from 'components/framework/controllers/concerns/HasSelection';
import {
  ConnectedPage,
  Page,
  Project,
  SharedPage,
  UserPreference,
} from 'components/framework/models';
import { type PageAttributes } from 'components/framework/models/generated/PageAttributes';
import {
  type DocumentTypeEnum_EXTERNAL_LINK,
  type DocumentTypeEnum_INTERNAL_LINK,
  type DocumentTypeEnum_value,
  type RecordId,
} from 'components/framework/models/generated/types';
import {
  type TemplateControllerProps,
  type TemplateType,
} from 'components/page_templates/TemplateController';
import type PageTreeController from 'components/page_tree/PageTreeController';
import { type AhaEventMap } from 'global_events';
import AhaAnalytics from 'javascripts/analytics';
import { csrfFetch } from 'javascripts/csrf_token';
import AhaDrawer from 'javascripts/drawer';
import { isPWA } from 'javascripts/pwa/pwa_utils';
import ReactiveRegister from 'javascripts/reactive_register';
import { isSmallScreen } from 'javascripts/utils';
import { camelCase, debounce } from 'lodash';
import moment, { type Moment } from 'moment';
import { actionLogger } from '../../framework/actionLogger';
import RemoteDocument from '../models/RemoteDocument';
import PageHistory, { CreateViews, createViewToPath } from './PageHistory';
// @ts-ignore
import Turbolinks from 'javascripts/turbolinks';
import { ToursController } from './ToursController';
import { getNestedPageId, setNestedPageId } from 'components/page_tree/helpers';
import { sharedSelectionController } from 'components/SelectionController/SelectionController';
import { type WhiteboardController } from '@aha-app/whiteboard';

interface State {
  // Currently selected project
  project: Project | null;
  // Currently selected page JSON data.
  page: Page | null;
  // Whether we are currently loading a page. This can be the partial page that is loading.
  pageLoading: boolean | Page | ConnectedPage;
  // Set in actionSetProject before making the graphQl request
  projectLoading: boolean;
  // If we were viewing a page and it got deleted then this becomes true
  pageDeleted: boolean;
  // Available templates.
  templateKeys: string[];
  // List of pages in the left sidebar.
  pages: Page[];
  // List of shared pages in the left sidebar and shared with me view
  connectedPages: ConnectedPage[];
  loadingConnectedPages: boolean;
  // List of pages for the selected date
  todaysPages: Page[];
  // Container for information about a shared page connection
  remoteDocument: RemoteDocument | null;
  // Whether we are currently loading the list of pages.
  pagesLoading: boolean;
  // Whether the sidebar is currently open.
  sidebarOpen: boolean;
  // Whether the sidebar will be pinned when open - controlled by the width
  // of the window.
  sidebarWillPin: boolean;
  // whether editor is fullscreen
  isFullScreen: boolean;
  // Which view is visible.
  currentView: CreateViews;

  // Whether link-based-document-sharing is available
  documentSharing: boolean;
  // Whether web-page based document-sharing is available
  webSharing: boolean;

  // Desktop only: available area in title bar
  titlebarAreaRect: any;
  // Desktop only: whether the overlay mode is active (collapsed titlebar)
  isTitlebarOverlayActive: boolean;
  // Message type to display in toast
  toastMessageType: string;

  // Calendar selected date.
  selectedDate: moment.Moment;

  showTemplateModal: boolean;
  templateModalParams: TemplateControllerProps;

  history: PageHistory;
  isBillingAdmin: boolean;
  isAdministrator: boolean;
  isConfigurationAdmin: boolean;
  isGuest: boolean;
  isReviewer: boolean;
  hasStandaloneAdvancedFeatures: boolean;
  sharePopupVisible: boolean;
  fullPage: Page;
  settingsTab: string;
  isDesktopApp: boolean;
  // Can install the desktop app
  canInstall?: boolean;

  zendeskIconVisible: boolean;
  canCreatePages: boolean;
  canRestrictAccess: boolean;
  isRoadmaps: boolean;
  hasRoadmapsFlavor: boolean;
  hasIdeasFlavor: boolean;
  hasTeamworkOrDevelopFlavor: boolean;
  isTeamworkOrDevelop: boolean;
  persistedView: CreateViews;

  // This adds the CSS transition between widths (narrow or full)
  fullWidthEditor?: boolean;
}

interface Props {
  initialProject: any;
  templateKeys: any;
  initialPageId: any;
  documentSharing: any;
  webSharing: boolean;
  isBillingAdmin: boolean;
  isAdministrator: boolean;
  isConfigurationAdmin: boolean;
  isGuest: boolean;
  isReviewer: boolean;
  hasStandaloneAdvancedFeatures: any;
  zendeskIconVisible: any;
  isRoadmaps: any;
  hasRoadmapsFlavor: any;
  hasIdeasFlavor: any;
  hasTeamworkOrDevelopFlavor: any;
  isTeamworkOrDevelop: any;
  persistedView: any;
  canCreatePersonalPages: boolean;
  canCreatePersonalTodos: boolean;
  canRestrictAccess: boolean;
  sidebarWidth: number;
  // Records section
  allRecordsSearchValue: string;
  allRecordsSelectedProductIds: string[];
  allRecordsProductsFetchURL: string;
  allRecordsTypeOptions: { text: string; value: string }[];
  allRecordsSelectedType: string[];
  allRecordsSelectedUser: { text: string; value: string };
  allRecordsUsersFetchURL: string;
  allRecordsSortOptions: Array<{ [key: string]: string }>;
  allRecordsSelectedSort: { text: string; value: string };
}

interface ShowTemplateModalOptions {
  relativePage?: Page;
  initialTemplateId?: RecordId;
  parentId?: RecordId;
  projectId?: RecordId;
}

interface DesktopNotesController
  extends IRoutingBehaviors,
    IHasReactiveRailsBehaviors,
    IHasSelection {
  pageSubscriptions: string[];
  tokenTimer: ReturnType<typeof setTimeout>;
  observer: MutationObserver;
  capturedInstallEvent?: Event & {
    prompt(): void;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  };
  deleteBtn?: HTMLElement;
  lastWidth?: number;
  userPageTreeController?: PageTreeController;
  watchedPageTreeController?: PageTreeController;
  canCreatePersonalPages: boolean;
  canCreatePersonalTodos: boolean;
  sidebarWidth: number;
}

@actionLogger('DesktopNotesController')
@RoutingBehaviors
@HasReactiveRailsBehaviors
class DesktopNotesController extends ApplicationController<State, Props> {
  static TOAST_TYPE_SCREEN_SIZE = 'screenSize';

  // Width above which sidebar will "pin" to the background rather than floating.
  static SIDEBAR_PIN_WIDTH = 950;

  static initialState: State = {
    // Currently selected project
    project: null,
    // Currently selected page JSON data.
    page: null,
    // Whether we are currently loading a page.
    pageLoading: false,
    // Set in actionSetProject before making the graphQl request
    projectLoading: false,
    // Whether we are viewing a page that has been deleted.
    pageDeleted: false,
    // Available templates.
    templateKeys: [],
    // List of pages in the left sidebar.
    pages: [],
    // List of shared pages in the left sidebar.
    connectedPages: [],
    loadingConnectedPages: true,
    // Container for information about a shared page connection
    remoteDocument: null,
    // Whether we are currently loading the list of pages.
    pagesLoading: true,
    // Whether the sidebar is currently open.
    sidebarOpen: true,
    // Whether the sidebar will be pinned when open - controlled by the width
    // of the window.
    sidebarWillPin: true,
    // Whether the sidebar is currently hidden.
    isFullScreen: false,
    // Which view is visible.
    currentView: null,

    // Whether link-based-document-sharing is available
    documentSharing: false,
    // Whether web-page based document sharing is available
    webSharing: false,

    // Desktop only: available area in title bar
    titlebarAreaRect: null,
    // Desktop only: whether the overlay mode is active (collapsed titlebar)
    isTitlebarOverlayActive: false,
    // Message type to display in toast
    toastMessageType: '',
    // Whether to show the template picker modal
    showTemplateModal: false,
    // Params for the page being created via the template modal
    templateModalParams: null,

    history: null,
    isBillingAdmin: false,
    isAdministrator: false,
    isConfigurationAdmin: false,
    isGuest: false,
    isReviewer: false,
    hasStandaloneAdvancedFeatures: false,
    sharePopupVisible: false,
    fullPage: null,
    settingsTab: null,
    isDesktopApp: false,
    zendeskIconVisible: false,

    // Calendar selected date.
    selectedDate: moment().startOf('day'),

    // List of pages for the relevant docs section on the my work page
    todaysPages: [],
    canCreatePages: false,
    canRestrictAccess: false,
    isRoadmaps: false,
    hasRoadmapsFlavor: false,
    hasIdeasFlavor: false,
    hasTeamworkOrDevelopFlavor: false,
    isTeamworkOrDevelop: false,
    persistedView: CreateViews.MyWork,
  };

  async initialize({
    templateKeys,
    initialPageId,
    documentSharing,
    webSharing,
    isBillingAdmin,
    isAdministrator,
    isConfigurationAdmin,
    isGuest,
    isReviewer,
    hasStandaloneAdvancedFeatures,
    zendeskIconVisible,
    isRoadmaps,
    hasRoadmapsFlavor,
    hasIdeasFlavor,
    hasTeamworkOrDevelopFlavor,
    isTeamworkOrDevelop,
    persistedView,
    canCreatePersonalPages,
    canCreatePersonalTodos,
    canRestrictAccess,
    sidebarWidth,
  }: Props) {
    this.state.project = null; // Only personal notes for now initialProject;
    this.state.templateKeys = templateKeys;
    this.state.history = new PageHistory();
    this.state.documentSharing = documentSharing;
    this.state.webSharing = webSharing;
    this.state.isBillingAdmin = isBillingAdmin;
    this.state.isAdministrator = isAdministrator;
    this.state.isConfigurationAdmin = isConfigurationAdmin;
    this.state.isGuest = isGuest;
    this.state.isReviewer = isReviewer;
    this.state.hasStandaloneAdvancedFeatures = hasStandaloneAdvancedFeatures;
    this.pageSubscriptions = [];
    this.state.isRoadmaps = Boolean(isRoadmaps);
    this.state.hasRoadmapsFlavor = Boolean(hasRoadmapsFlavor);
    this.state.hasIdeasFlavor = Boolean(hasIdeasFlavor);
    this.state.hasTeamworkOrDevelopFlavor = Boolean(hasTeamworkOrDevelopFlavor);
    this.state.isTeamworkOrDevelop = Boolean(isTeamworkOrDevelop);
    this.state.persistedView = persistedView;
    this.state.zendeskIconVisible = zendeskIconVisible;

    this.canCreatePersonalPages = canCreatePersonalPages;
    this.canCreatePersonalTodos = canCreatePersonalTodos;
    this.state.canCreatePages = canCreatePersonalPages;
    this.state.canRestrictAccess = canRestrictAccess;

    this.sidebarWidth = sidebarWidth;

    this.addDependencyLines = this.addDependencyLines.bind(this);
    this.removeDependencyLines = this.removeDependencyLines.bind(this);
    this.refreshDependencyLines = this.refreshDependencyLines.bind(this);

    if (initialPageId) {
      await this.actionLoadPageById(initialPageId);
    }

    if (window.ahaDesktopNotes) {
      window.ahaDesktopNotes.onLoadPage(async pageId => {
        await this.actionLoadPageById(pageId);
      });
    }

    this.initPWA();

    if (documentSharing) {
      await this.actionRefreshConnectedPages();
      this.subscribeToSharedDocuments();
    }

    if (await this.shouldShowScreenSizeWarning()) {
      this.actionShowOnlyCreateToast(
        DesktopNotesController.TOAST_TYPE_SCREEN_SIZE
      );
    }

    document.addEventListener(
      'aha.fred.result-selected',
      this.handleFredResultSelected
    );
    document.addEventListener('aha.redirect_to', this.handleRedirect);
    document.addEventListener('aha.set_project', this.handleSetProject);
    document.addEventListener('aha.destroy', this.handleDestroy);

    await this.actionLoadPagesForSelectedDate();
    this.subscribeBatch(`User-${window.currentUser?.id}/Page-`, async () => {
      await this.actionLoadPagesForSelectedDate();
    });
  }

  destroy() {
    clearTimeout(this.tokenTimer);
    // eslint-disable-next-line no-unused-expressions
    this.observer?.disconnect();

    document.removeEventListener(
      'aha.fred.result-selected',
      this.handleFredResultSelected
    );
    document.removeEventListener('aha.redirect_to', this.handleRedirect);
    document.removeEventListener('aha.set_project', this.handleSetProject);
    document.removeEventListener('aha.destroy', this.handleDestroy);
  }

  get viewingSharedDocument() {
    return !!(
      this.state.currentView === CreateViews.Document &&
      this.state.remoteDocument
    );
  }

  // This controller is not in the same hierarch as the whitebaord, so in
  // order to get access to the whiteboard controller we need to get it from
  // the DOM where the whiteboard controller stored a reference to itself.
  get whiteboardController() {
    const elementStoringWhiteboardController = document.querySelector(
      '.desktop-notes-app [data-whiteboard-controller-store-element]'
    );

    if (
      !elementStoringWhiteboardController ||
      !('whiteboardController' in elementStoringWhiteboardController)
    ) {
      console.warn(
        'No whiteboard controller found. It should have been stored on the whiteboard container already.',
        elementStoringWhiteboardController
      );
      return null;
    }

    return elementStoringWhiteboardController.whiteboardController as WhiteboardController;
  }

  get menuState() {
    return (
      this.whiteboardController?.moreMenuActionsState() || {
        addAvailable: false,
        removeAvailable: false,
      }
    );
  }

  addDependencyLines() {
    this.whiteboardController?.actionAddDependencyLines();
  }

  removeDependencyLines() {
    this.whiteboardController?.actionRemoveDependencyLines();
  }

  refreshDependencyLines() {
    this.whiteboardController?.actionRefreshDependencyLines();
  }

  async actionInstall() {
    if (!this.state.canInstall) {
      return;
    }
    if (this.capturedInstallEvent) {
      this.capturedInstallEvent.prompt();
      const { outcome } = await this.capturedInstallEvent.userChoice;
      if (outcome === 'accepted') {
        this.capturedInstallEvent = undefined;
      }
    }
  }

  actionGetIsDesktop() {
    const isDesktopApp = isPWA();
    this.state.isDesktopApp = isDesktopApp;
    return this.state.isDesktopApp;
  }

  _handleBeforeInstallPrompt(e) {
    this.capturedInstallEvent = e;
    this.actionSetCanInstall(e && !this.actionGetIsDesktop());
  }

  _handleAppInstalled() {
    this.actionSetCanInstall(false);
  }

  initPWA() {
    // Determine if we're in the installed App
    const isDesktopApp = this.actionGetIsDesktop();

    this._handleBeforeInstallPrompt =
      this._handleBeforeInstallPrompt.bind(this);
    this._handleAppInstalled = this._handleAppInstalled.bind(this);

    // Capture the install prompt event for later use
    window.addEventListener('beforeinstallprompt', e =>
      this._handleBeforeInstallPrompt(e)
    );
    // Listen for install event
    window.addEventListener('appinstalled', this._handleAppInstalled);

    if (isDesktopApp) {
      if ('windowControlsOverlay' in navigator) {
        const windowControlsOverlay = navigator.windowControlsOverlay as any;

        this.actionTitlebarAreaChanged({}, windowControlsOverlay.visible);
        windowControlsOverlay.ongeometrychange = debounce(e => {
          this.actionTitlebarAreaChanged(
            e.titlebarAreaRect,
            windowControlsOverlay.visible
          );
        }, 150);
      }
    }
  }

  actionTitlebarAreaChanged(areaRect, overlayActive) {
    this.state.titlebarAreaRect = areaRect;
    this.state.isTitlebarOverlayActive = overlayActive;
  }

  actionSetCanInstall(canInstall) {
    this.state.canInstall = canInstall;
  }

  subscribeToDocuments() {
    const processReactiveChange = async change => {
      const { action, path } = change;
      const isPage = path.includes('Page');
      const isConnectedPage = path.includes('ConnectedPage');
      if (isPage && action !== 'update') {
        await this.actionLoadAllPages();
      }
      if ((isPage || isConnectedPage) && action === 'create') {
        this.actionShowOnlyCreateToast();
      }
    };

    ReactiveRegister.register(
      'unique-id-all-notifications',
      `User-${window.currentUser.id}/`,
      async change => {
        await processReactiveChange(change);
      }
    );
  }

  subscribeToSharedDocuments() {
    this.subscribeBatch(
      `User-${window.currentUser.id}/ConnectedPage-`,
      async change => {
        await this.actionRefreshConnectedPages(change);
      },
      {}
    );
  }

  subscribeToPage(page = this.state.page) {
    this.pageSubscriptions.forEach(sub => this.unsubscribe(sub));
    this.pageSubscriptions.length = 0;

    let reactivePath;
    if (page) {
      let parentPath;
      if (page.project) {
        parentPath = `Project-${page.project.id}`;
      } else {
        parentPath = `User-${window.currentUser.id}`;
      }

      reactivePath = `${parentPath}/Page-${page.id}`;

      this.pageSubscriptions.push(
        this.subscribeBatch(reactivePath, async updates => {
          const relevantUpdates = updates.filter(({ path }) =>
            path.includes(page.reactiveId)
          );

          if (relevantUpdates.length > 0) {
            const destroyed = relevantUpdates.find(
              ({ action }) => action === 'destroy'
            );

            if (destroyed) {
              this.state.pageDeleted = true;
            } else {
              await this.refreshPage(page, false);
            }
          }
        })
      );

      this.pageSubscriptions.push(
        this.subscribeBatch(`Page-${page.id}/SharedPage-`, async () => {
          await this.refreshPage(page);
        })
      );

      this.pageSubscriptions.push(
        this.subscribeBatch(`Page-${page.id}/PageShareLink-`, async () => {
          await this.refreshPage(page);
        })
      );
    }
  }

  actionSetWidth(width) {
    // When DesktopNotes is rendered in the roadmaps (Roadmaps, Develops and Ideas)
    // context we dont want to change the sidebar.
    if (this.state.isRoadmaps) return;
    // We close the sidebar if the width is being reduced over the pin break,
    // but don't close it again if it was opened while the window was
    // already small.
    if (
      this.lastWidth > DesktopNotesController.SIDEBAR_PIN_WIDTH &&
      width <= DesktopNotesController.SIDEBAR_PIN_WIDTH
    ) {
      this.state.sidebarOpen = false;
    }
    this.state.sidebarWillPin =
      width > DesktopNotesController.SIDEBAR_PIN_WIDTH;
    this.lastWidth = width;
  }

  actionCollapseSidebar() {
    // If the sidebar is not pinned open, then collapse it.
    if (!this.state.sidebarWillPin) {
      this.state.sidebarOpen = false;
    }
  }

  actionToggleSidebar() {
    this.state.sidebarOpen = !this.state.sidebarOpen;
  }

  actionSetIsFullScreen(hidden: boolean) {
    this.state.isFullScreen = hidden;
  }

  async actionSetCurrentView(view: CreateViews, updateHistory = true) {
    // Guests can only see shared documents.
    if (
      this.state.isGuest &&
      ![
        CreateViews.Shared,
        CreateViews.Document,
        CreateViews.Settings,
      ].includes(view)
    ) {
      view = CreateViews.Shared;
    }

    this.state.currentView = view;
    this.actionSetIsFullScreen(false);
    this.state.pageDeleted = false;
    this.actionPersistView(view);

    if (view !== CreateViews.Document) {
      this.clearPage();
      this.updateDocumentTitle(this.state.isRoadmaps ? 'My work' : '');
    }

    if (view === CreateViews.Settings) {
      this.state.sidebarOpen = false;
    }

    if (updateHistory) {
      this.state.history.visitPage({
        view,
        page: this.state.page,
        project: this.state.project,
      });
    }
  }

  async actionSetPersistedView() {
    const { persistedView } = this.state;
    if (this.state.isRoadmaps) {
      if (Object.values(CreateViews).includes(persistedView)) {
        this.actionSetCurrentView(persistedView);
      } else {
        this.actionSetCurrentView(CreateViews.MyWork);
      }
    }
  }

  async actionPersistView(view: CreateViews) {
    if (!this.state.isRoadmaps) return;
    let parsedView = view;
    switch (view) {
      case CreateViews.Document:
        parsedView = CreateViews.Documents;
    }
    const persistedViews = [
      CreateViews.MyWork,
      CreateViews.Todos,
      CreateViews.Tasks,
      CreateViews.Records,
      CreateViews.Initiatives,
      CreateViews.Releases,
      CreateViews.Features,
      CreateViews.Ideas,
      CreateViews.Documents,
      CreateViews.Recents,
      CreateViews.Shared,
      CreateViews.Meetings,
    ];
    if (persistedViews.includes(parsedView)) {
      csrfFetch('/user_dashboard', {
        method: 'PUT',
        body: JSON.stringify({ section: parsedView }),
      });
    }
  }

  async actionSetProject(project, clearCurrentPage = true) {
    if (project?.id) {
      if (project.id !== this.state.project?.id) {
        this.state.projectLoading = true;
        const newProject = await Project.select(
          'id',
          'name',
          'referencePrefix',
          'canCreatePages'
        )
          .merge({
            projectPermissions: ['createNoteTemplates'],
          })
          .find(project.id);

        const newState: Partial<State> = {
          project: newProject,
          canCreatePages: newProject.canCreatePages,
          projectLoading: false,
        };

        if (clearCurrentPage) {
          newState.currentView = CreateViews.Documents;
        }

        this.setState(newState);
      }
    } else {
      this.setState({
        project: null,
        canCreatePages: this.canCreatePersonalPages,
      });
    }

    if (clearCurrentPage) {
      await this.actionLoadPage(null);
    }
  }

  unloadRemoteDocument() {
    if (this.state.remoteDocument) {
      this.state.remoteDocument.unload();
      this.state.remoteDocument = null;
    }
  }

  clearPage() {
    this.unloadRemoteDocument();
    this.state.page = null;
    this.state.pageLoading = false;
    this.state.pageDeleted = false;
  }

  actionLoadPageById(pageId: string, newTab = false) {
    if (
      pageId === this.state.page?.id ||
      pageId === this.state.page?.referenceNum
    ) {
      // We are already viewing this page, no need to
      // reload. Otherwise, anchor links would cause a page reload and
      // may not work.
      return;
    }

    return this.actionLoadPage(new Page({ id: pageId }), newTab);
  }

  async actionLoadPage(
    newPage: Page | null,
    newTab = false,
    updateHistory = true
  ) {
    if (newPage && this.shouldOpenProjectNotesInProject(newPage)) {
      return (window.location.href = `/pages/${newPage.referenceNum}`);
    }
    if (newPage && newTab) {
      window.open(`/notebooks/pages/${newPage.id}`, '_blank').opener = null;
      return;
    }

    if (!newPage) {
      this.clearPage();
      return;
    }

    this.actionCollapseSidebar();

    this.actionSetCurrentView(CreateViews.Document, updateHistory);

    this.state.fullWidthEditor =
      newPage?.editorWidth === Page.EDITOR_WIDTH_FULL;
    if (!this.state.pageLoading) this.state.pageLoading = newPage;

    this.unloadRemoteDocument();

    // Placeholder while we load the page. This is to ensure the page stays
    // selected in the sidebar PageTree.
    this.state.page = new Page({
      id: newPage.id,
      referenceNum: newPage.referenceNum,
      documentType: newPage.documentType,
      documentDate: newPage.documentDate,
      name: newPage.name,
      project: newPage.project,
    });
    this.state.sharePopupVisible = true;

    // We don't want to navigate if we're loading a nested page
    await this.refreshPage(newPage, !getNestedPageId(), updateHistory);
    if (!this.state.page.isExternalLink) {
      await this.state.page.trackViewed();
    }
  }

  async actionInsertTemplatePage(page: Page, isDailyPage = false) {
    // If we inserted a new page into another project then visit the page
    // directly to get to the workspace document view
    if (page.project && page.project.id !== this.state.project?.id) {
      return Turbolinks.visit(page.path);
    }

    await this.addNewPage({ page, isDailyPage });
    if (this.userPageTreeController)
      this.userPageTreeController.actionShowPage(page);
  }

  async actionLoadAllPages() {
    this.state.pagesLoading = true;
    this.state.pages = await DesktopNotesController.pagesScope
      .where({ personal: true })
      .per(100)
      .findInBatches();
    this.state.pagesLoading = false;
  }

  async actionLoadPagesForSelectedDate() {
    this.state.pagesLoading = true;
    this.state.todaysPages = await DesktopNotesController.pagesScope
      .where({
        documentDate: this.state.selectedDate.toISOString(),
        watched: true,
      })
      .per(100)
      .findInBatches();
    this.state.pagesLoading = false;
  }

  static get pagesScope() {
    return Page.select(
      'id',
      'name',
      'path',
      'documentType',
      'documentDate',
      'position',
      'referenceNum',
      'emojiValue',
      'sharedPagesCount',
      'isShared',
      'editorWidth',
      'editLocked',
      'showContents',
      'childrenCount',
      'externalUrl',
      'linkedRecordId',
      'linkedRecordType',
      'linkedDocumentType',
      'internalPath',
      'ideaPortalsCount'
    ).merge({
      parent: ['id', 'referenceNum', 'name', 'documentType'],
      project: Project.select(['id', 'name', 'referencePrefix']).merge({
        projectPermissions: ['createNoteTemplates'],
      }),
      user: ['id'],
      activeShareLink: ['id', 'access', 'expiresAt', 'url'],
      permissions: ['duplicate', 'destroy', 'update'],
    });
  }

  get pageScope() {
    return DesktopNotesController.pagesScope.select('readOnly').merge({
      export: ['path', 'httpMethod'],
      addTo: ['addToWhiteboardAsImagePath', 'addToPresentationPath'],
      // TODO: This forces description to be instantiated.
      description: [
        'id',
        'editorProperties',
        'canReviewNote',
        'canUpdateNote',
        'usedTemplateIds',
      ],
      whiteboard: [
        'id',
        'editorContent',
        'collaborativeVersion',
        'usedTemplateIds',
      ],
      watchers: ['id', 'name', 'initials', 'avatarUrl(size: SIZE_16)'],
      sharedPages: SharedPage.select(['id', 'access']).merge({
        user: ['id', 'name', 'email', 'avatarUrl'],
      }),
      permissions: ['duplicate', 'destroy', 'update'],
    });
  }

  async actionRefreshConnectedPages(_change?: any) {
    this.state.loadingConnectedPages = true;
    this.state.connectedPages = await ConnectedPage.select(
      'id',
      'name',
      'documentType',
      'lastAccessedAt'
    ).findInBatches();
    this.state.loadingConnectedPages = false;

    if (this.state.remoteDocument) {
      const { connectedPage } = this.state.remoteDocument;
      if (connectedPage) {
        const index = this.state.connectedPages.findIndex(
          cp => cp.id === connectedPage.id
        );

        if (index === -1) {
          this.clearPage();
        } else {
          this.state.remoteDocument.update(this.state.connectedPages[index]);
          this.state.page = this.state.remoteDocument.page;
          this.updateDocumentTitle(this.state.page.nameOrDefault);
        }
      }
    }
  }

  async actionReloadCollaborators() {
    await this.state.remoteDocument.reloadCollaborators();
  }

  async actionLoadSharedPageById(connectedPageId) {
    if (this.state.remoteDocument?.connectedPage?.id === connectedPageId) {
      // We are already viewing this connected page, no need to
      // reload. This can happen when navigating to a hash.
      return;
    }

    return this.actionLoadSharedPage(
      new ConnectedPage({ id: connectedPageId })
    );
  }

  async actionLoadSharedPage(
    connectedPage: ConnectedPage,
    updateHistory = true
  ) {
    this.state.pageLoading = connectedPage;
    this.state.sharePopupVisible = false;

    if (updateHistory) {
      this.state.history.visitPage({
        view: CreateViews.Document,
        page: connectedPage,
        project: this.state.project,
      });
    }

    this.unloadRemoteDocument();
    this.state.remoteDocument = new RemoteDocument(connectedPage);
    const { remoteDocument } = this.state;
    this.state.page = remoteDocument.page;

    await this.actionSetCurrentView(CreateViews.Document, false);
    await remoteDocument.load();

    if (remoteDocument.isLoaded) {
      this.state.fullWidthEditor =
        this.state.page.editorWidth === Page.EDITOR_WIDTH_FULL;
      this.updateDocumentTitle(remoteDocument.page.nameOrDefault);
    }

    this.state.pageLoading = false;
  }

  async actionDeleteConnectedPage() {
    await this.state.remoteDocument.delete();
  }

  async actionRefreshAll() {
    await this.refreshPage(this.state.page);
    this.refreshPageTree();
  }

  private _loadPageCounter = 0;
  async refreshPage(
    pageData: Page | Partial<PageAttributes>,
    shouldNavigate = true,
    updateHistory = true
  ) {
    const counter = ++this._loadPageCounter;
    const page = await this.pageScope.find(pageData.id);

    // If we start loading the page again then ignore the previous loaded data
    if (counter !== this._loadPageCounter) {
      return;
    }

    this.state.fullWidthEditor = page.editorWidth === Page.EDITOR_WIDTH_FULL;
    this.state.fullPage = page;

    if (shouldNavigate) {
      this.setPage(page, updateHistory);
    } else {
      this.state.page = page;
      this.updateDocumentTitle(page.nameOrDefault);
    }
    this.state.pageLoading = false;
  }

  setPage(page: Page, updateHistory = true) {
    // Sync the native app state.
    if (window.ahaDesktopNotes) {
      window.ahaDesktopNotes.setTitle(page.nameOrDefault);
      window.ahaDesktopNotes.saveUrl(
        `aha-notes://${window.location.host}/pages/${page.id}`
      );
    } else {
      this.updateDocumentTitle(page.nameOrDefault);
    }

    this.state.fullWidthEditor = page.editorWidth === Page.EDITOR_WIDTH_FULL;
    this.state.page = page;
    this.actionSetCurrentView(CreateViews.Document, false);
    this.subscribeToPage();

    const internalPageParent =
      this.userPageTreeController?.state.pageHierarchy.internalPageParent(
        page.id
      );

    if (!internalPageParent) {
      this.actionSetProject(page.project, false);
    }

    if (updateHistory && !internalPageParent) {
      this.state.history.visitPage({
        view: CreateViews.Document,
        page,
        project: page.project,
      });
    }
  }

  async refreshPageTree() {
    if (this.userPageTreeController)
      await this.userPageTreeController.reloadAllPages();
    if (this.watchedPageTreeController)
      await this.watchedPageTreeController.reloadAllPages();
  }

  actionGetActivePageId() {
    return this.state.page?.id;
  }

  actionSetActivePageId(pageId) {
    this.state.page = new Page({ id: pageId });
  }

  actionSetActivePage(page) {
    this.state.page = page;
    this.state.remoteDocument.unload();
    this.subscribeToPage(page);
  }

  async actionSetPageName(page: Page, value: string) {
    page.name = value;
    this.updateDocumentTitle(page.nameOrDefault);
    await page.save();
  }

  actionRemovePage(page: Page) {
    if (this.userPageTreeController) {
      this.userPageTreeController.state.pageHierarchy.removePage(page);
    }

    if (page.id === this.state.page?.id) {
      if (this.userPageTreeController) {
        this.userPageTreeController.state.activePageId = null;
      }

      this.clearPage();
      this.subscribeToPage(null);

      const url = window.location.href.split('/').slice(0, -1).join('/');
      window.history.pushState({ url: url }, '', url);
    }
  }

  async actionCreateFolder({ relativePage }: { relativePage?: Page }) {
    const parentId = relativePage?.parent?.id;
    if (relativePage?.documentType === Page.DOCUMENT_TYPE_FOLDER) {
      // Folders should not be relative to non-folders by
      // default, actionCreatePage will find a good place
      // for them.

      relativePage = null;
    }

    return this.actionCreatePage({
      documentType: Page.DOCUMENT_TYPE_FOLDER,
      relativePage,
      parentId,
    });
  }

  async actionCreatePage({
    documentType,
    editorVersion,
    relativePage,
    parentId,
    ...attributes
  }: {
    documentType: DocumentTypeEnum_value;
    editorVersion?: number;
    relativePage?: Page;
    parentId?: string;
  } & Partial<PageAttributes>) {
    const page = new Page(
      {
        documentType,
        editorVersion,
        documentDate: this.state.selectedDate.format('YYYY-MM-DD'),
        ...attributes,
      },
      { query: this.pageScope.first() }
    );
    const isDailyPage = false;

    if (this.state.project) {
      page.project = new Project({ id: this.state.project.id });
    }

    if (relativePage?.parent) {
      page.parent = new Page({ id: relativePage.parent.id });
    }

    if (parentId) {
      page.parent = new Page({ id: parentId });
    }

    sharedSelectionController.reset();

    await this.addNewPage({ page, relativePage, isDailyPage });
  }

  async addNewPage({ page, relativePage = null, isDailyPage }) {
    const addedPage = await this.userPageTreeController?.actionAddPage(
      page,
      relativePage
    );
    this.state.pageLoading = page.documentType || 'unknown';
    this.state.page = addedPage;

    addedPage.synchronized();
    await this.actionLoadPage(page);

    if (isDailyPage) {
      // Add to the daily pages folder after it is created so we don't slow down
      // the creation process.
      this.addToDailyNotesFolder(page);
    }
  }

  async actionSetCalendarDate(date) {
    this.clearPage();

    this.state.selectedDate = moment(date);
  }

  actionShowTemplateModal(
    type: TemplateType = 'all',
    options: ShowTemplateModalOptions = {}
  ) {
    const { relativePage, initialTemplateId, parentId, projectId } = options;
    const params: Record<string, any> = {};

    if (relativePage) {
      params.relative_page_id = relativePage.id;
      if (relativePage.parent) {
        params.parent_id = relativePage.parent.id;
      }

      params.position =
        this.userPageTreeController?.state.pageHierarchy.nextPosition(
          relativePage
        ) || 0;
    }

    if (parentId) {
      params.parent_id = parentId;
    }

    if (this.state.currentView === CreateViews.MyWork) {
      params.document_date = moment(this.state.selectedDate.toISOString())
        .toISOString(true)
        .substring(0, 10);
      params.is_daily_page = true;
    } else if (this.state.project) {
      params.project_id = this.state.project.id;
    }

    // ProjectId is optionally passed, for example by the meetings list page
    if (projectId) {
      params.project_id = projectId;
    }
    params.template_type = type;
    params.initialTemplateId = initialTemplateId;

    this.state.templateModalParams = {} as any;
    for (const [key, value] of Object.entries(params)) {
      this.state.templateModalParams[camelCase(key)] = value;
    }
    this.state.showTemplateModal = true;
  }

  actionHideTemplateModal() {
    this.state.showTemplateModal = false;
  }

  actionShowPageLinkModal(
    linkType: DocumentTypeEnum_EXTERNAL_LINK | DocumentTypeEnum_INTERNAL_LINK
  ) {
    const projectId = this.state.project?.id;
    let url = '/user/pages/new.js';

    if (projectId) {
      url = `/products/${projectId}/pages/new.js`;
    }

    let position;
    const relativePage = this.state.page;

    if (relativePage) {
      position =
        this.userPageTreeController?.state.pageHierarchy.nextPosition(
          relativePage
        );
    }

    return new Promise((resolve, reject) =>
      $.ajax({
        url,
        type: 'GET',
        dataType: 'script',
        data: {
          document_type: linkType,
          parent_id: relativePage?.parent?.id,
          product_id: projectId,
          position,
        },
      })
        .done(resolve)
        .fail(reject)
        .catch(reject)
    );
  }

  actionHistoryGoBack() {
    if (this.state.history.hasBack()) {
      const { view, page, project } = this.state.history.goBack();

      if (page) {
        this.actionNavigatePage(page, false, false);
      } else if (view) {
        this.actionSetCurrentView(view, false);
      }

      this.actionSetProject(project, false);
    }
  }

  actionHistoryGoForward() {
    if (this.state.history.hasForward()) {
      const { view, page, project } = this.state.history.goForward();

      if (page) {
        this.actionNavigatePage(page, false, false);
      } else if (view) {
        this.actionSetCurrentView(view, false);
      }

      this.actionSetProject(project, false);
    }
  }

  actionSetUserPageTreeController(pageTreeController: PageTreeController) {
    this.userPageTreeController = pageTreeController;
  }

  actionSetWatchedPageTreeController(pageTreeController) {
    this.watchedPageTreeController = pageTreeController;
  }

  actionShowOnlyCreateToast(messageType = '') {
    if (messageType === DesktopNotesController.TOAST_TYPE_SCREEN_SIZE) {
      this.state.toastMessageType =
        DesktopNotesController.TOAST_TYPE_SCREEN_SIZE;
    } else {
      console.error('Unsupported message type');
      this.state.toastMessageType = '';
    }
  }

  async actionClearOnlyCreateToast() {
    // If user dismisses screen size warning, save the time to check against
    // the next time the page loads
    if (
      this.state.toastMessageType ===
      DesktopNotesController.TOAST_TYPE_SCREEN_SIZE
    ) {
      await UserPreference.setUserPreference(
        'user.pages.screenSizeWarningDismissalTime',
        Date.now().toString()
      );
    }
    this.state.toastMessageType = '';
  }

  actionGoToBillingPage() {
    window.location.href = '/notebooks/settings/billing';
  }

  // Add the page to the daily notes folder, creating it if necessary.
  async addToDailyNotesFolder(page) {
    const folder = new Page({
      documentType: Page.DOCUMENT_TYPE_DAILY_NOTES_FOLDER,
    });
    await folder.save();
    page.parent = folder;
    await page.save();
  }

  pagesURL() {
    if (this.state.project) {
      return `/notebooks/projects/${this.state.project.id}/pages.json`;
    } else {
      return `/notebooks/user/pages.json`;
    }
  }

  /**
   * Construct a path to a document for a given page or reference num
   *
   * - If isRoadmaps is true then this is the new design for my work under /user_dashboard
   * - Otherwise /notebooks
   */
  pageUrl(page: Page | ConnectedPage | string) {
    let rootSegment: string;
    let documentSegment: string = 'documents';
    let referenceNum: string;
    let queryParams: string;
    let insertProject = !!this.state.project;

    if (this.state.isRoadmaps) {
      rootSegment = '/user_dashboard';
    } else {
      rootSegment = '/notebooks';
    }

    if (typeof page === 'string') {
      referenceNum = page;
    } else if (page.typename === 'ConnectedPage') {
      documentSegment += '/shared';
      referenceNum = page.id;
      insertProject = false;
    } else {
      const internalPageParent =
        this.userPageTreeController?.state.pageHierarchy.internalPageParent(
          page?.id
        );

      if (internalPageParent) {
        const urlParams = setNestedPageId(page?.referenceNum || page?.id);
        queryParams = urlParams.toString();

        referenceNum = internalPageParent.referenceNum;
      } else {
        referenceNum = page.referenceNum;
      }
    }

    let path: string;

    if (insertProject) {
      path = [
        rootSegment,
        'projects',
        this.state.project.referencePrefix,
        'documents',
        referenceNum,
      ].join('/');
    } else if (
      typeof page !== 'string' &&
      this.shouldOpenProjectNotesInProject(page)
    ) {
      path = ['/pages', referenceNum].join('/');
    } else {
      path = [rootSegment, documentSegment, referenceNum].join('/');
    }

    if (queryParams) {
      return `${path}?${queryParams}`;
    } else {
      return path;
    }
  }

  /**
   * Construct a path to a view
   */
  viewUrl(view: CreateViews) {
    return this.viewUrlForProject(view, this.state.project?.referencePrefix);
  }

  viewUrlForProject(view: CreateViews, projectRef?: string) {
    const prefix = this.state.isRoadmaps ? '/user_dashboard' : '/notebooks';

    if (projectRef) {
      return [prefix, 'projects', projectRef, createViewToPath(view)].join('/');
    } else {
      return [prefix, createViewToPath(view)].join('/');
    }
  }

  async actionShowPersonalNotes() {
    this.actionSetProject(null);
    this.actionSetCurrentView(CreateViews.MyWork);
  }

  actionNavigatePage(
    page: Page | ConnectedPage,
    newTab?: boolean,
    updateHistory: boolean = true
  ) {
    const url = this.pageUrl(page);
    this.trackPageView(url);
    if (newTab) {
      window.open(url, '_blank').opener = null;
      return;
    }

    const toursController = this.findControllerInstance(ToursController);
    if (toursController) toursController.clearTour();

    this.state.pageLoading = page;
    history.pushState(null, null, url);

    if (page.typename === 'ConnectedPage') {
      return this.actionLoadSharedPage(page, updateHistory);
    } else {
      return this.actionLoadPage(page, false, updateHistory);
    }
  }

  /**
   * Navigate to a path as if the user clicked on a link. Returns false if we couldn't navigate
   */
  actionNavigate(path: string, newTab?: boolean) {
    this.trackPageView(path);
    if (newTab) {
      window.open(path, '_blank').opener = null;
      return true;
    }

    if (this.router.hasMatch(path)) {
      history.pushState(null, null, path);
      this.router.navigate(path);
      return true;
    }

    return false;
  }

  routes(router) {
    router.namespace('/notebooks', () => {
      router.add('/', () => this.actionSetCurrentView(CreateViews.MyWork));

      router.add('/mywork', () => {
        this.actionSetCurrentView(CreateViews.MyWork);
        this.actionSetProject(undefined);
      });

      router.add('/notifications', () =>
        this.actionSetCurrentView(CreateViews.Notifications)
      );

      router.add('/todos', () => this.actionSetCurrentView(CreateViews.Todos));

      router.add('/documents', () =>
        this.actionSetCurrentView(CreateViews.Documents)
      );

      router.namespace('/documents', () => {
        router.add('/templates', () => {
          this.actionSetCurrentView(CreateViews.Documents);
          this.actionShowTemplateModal();
        });
        router.add('/shared/:pageId', ({ pageId }) => {
          this.actionLoadSharedPageById(pageId);
        });
        router.add('/recents', () =>
          this.actionSetCurrentView(CreateViews.Recents)
        );
        router.add('/shared', () =>
          this.actionSetCurrentView(CreateViews.Shared)
        );
        router.add('/meetings', () => {
          this.actionSetCurrentView(CreateViews.Meetings);
        });
        router.add('/:pageId', ({ pageId }) => {
          const nestedPageId = getNestedPageId();
          this.actionLoadPageById(nestedPageId ?? pageId);
        });
      });

      // Not using namespaces here as we need a variable that's not at the end
      // of the route. These are also in a specific order, to avoid 'recents'
      // being interpreted as a page ID.
      router.add(
        '/projects/:projectId/documents/recents',
        async ({ projectId }) => {
          await this.actionSetProject(new Project({ id: projectId }));
          this.actionSetCurrentView(CreateViews.Recents);
        }
      );

      router.add(
        '/projects/:projectId/documents/:pageId',
        async ({ projectId, pageId }) => {
          await this.actionSetProject(new Project({ id: projectId }));
          this.actionLoadPageById(pageId);
        }
      );

      router.add('/projects/:projectId/documents', async ({ projectId }) => {
        await this.actionSetProject(new Project({ id: projectId }));
        this.actionSetCurrentView(CreateViews.Documents);
      });

      router.namespace('/settings', () => {
        router.add('/', () => this.actionShowSettings(null));
        router.add('/:tab', ({ tab }) => this.actionShowSettings(tab));
      });
    });

    // Empty routes to handle router no match errors in My Work
    router.namespace('/user/', () => {
      router.namespace('/pages', () => {
        router.add('/:pageId', ({ pageId }) => {
          this.actionLoadPageById(pageId);
        });
        router.add('/shared/:connectedPageId', ({ connectedPageId }) => {
          this.actionLoadSharedPageById(connectedPageId);
        });
      });
    });

    // Handle opening shared page links while in a Develop team
    router.add(
      '/develop/user/pages/shared/:connectedPageId',
      ({ connectedPageId }) => {
        this.actionLoadSharedPageById(connectedPageId);
      }
    );

    // Redirect the old user_dashboard routes to the new ones, and preserve any
    // url parameters
    router.namespace('/develop/user_dashboard', () => {
      router.add('/', () => this.actionSetPersistedView());

      router.add('*', ({ wildcard }) => {
        const route = `/user_dashboard/${wildcard}`;

        window.history.pushState(
          null,
          null,
          `${route}${window.location.search}`
        );

        // We need to reset the router's internal history so it picks up the
        // url modification from above
        this.router.resetHistory();
        this.router.navigate(route);
      });
    });

    router.namespace('/user_dashboard', () => {
      router.add('/', () => this.actionSetPersistedView());
      router.add('/mywork', () =>
        this.actionSetCurrentView(CreateViews.MyWork)
      );

      router.add('/notifications', () =>
        this.actionSetCurrentView(CreateViews.Notifications)
      );

      router.add('/meetings', () =>
        this.actionSetCurrentView(CreateViews.Meetings)
      );
      router.add('/tasks', () => this.actionSetCurrentView(CreateViews.Tasks));
      router.add('/all_work', () =>
        this.actionSetCurrentView(CreateViews.Records)
      );
      router.add('/initiatives', () =>
        this.actionSetCurrentView(CreateViews.Initiatives)
      );
      router.add('/features', () =>
        this.actionSetCurrentView(CreateViews.Features)
      );
      router.add('/releases', () =>
        this.actionSetCurrentView(CreateViews.Releases)
      );
      router.add('/ideas', () => this.actionSetCurrentView(CreateViews.Ideas));

      router.add('/documents', () =>
        this.actionSetCurrentView(CreateViews.Documents)
      );
      router.namespace('/documents', () => {
        router.add('/templates', () => {
          this.actionSetCurrentView(CreateViews.Documents);
          this.actionShowTemplateModal();
        });
        router.add('/shared/:pageId', ({ pageId }) => {
          this.actionLoadSharedPageById(pageId);
        });
        router.add('/recents', () =>
          this.actionSetCurrentView(CreateViews.Recents)
        );
        router.add('/shared', () =>
          this.actionSetCurrentView(CreateViews.Shared)
        );
        router.add('/:pageId', ({ pageId }) => {
          this.actionLoadPageById(pageId);
        });
      });
    });
  }

  stateToPath(path: PathBuilder) {
    const {
      currentView,
      page,
      remoteDocument,
      settingsTab,
      isRoadmaps,
      project,
    } = this.state;

    /**
     * If we update the path then maintain the hash
     */
    if (window.location.hash) {
      path.hash(window.location.hash);
    }

    path.segment(isRoadmaps ? '/user_dashboard' : '/notebooks');

    if (project?.referencePrefix) {
      path.segment(`/projects/${project.referencePrefix}`);
    }

    switch (currentView) {
      case CreateViews.MyWork:
        path.segment('/mywork');
        break;
      case CreateViews.Notifications:
        path.segment('/notifications');
        break;
      case CreateViews.Documents:
      case CreateViews.Document:
        path.segment('/documents');
        break;
      case CreateViews.Settings:
        path.segment('/settings');
        if (settingsTab) {
          path.segment(settingsTab);
        }
        break;
      case CreateViews.Meetings:
        path.segment('/meetings');
        break;
      case CreateViews.Todos:
        path.segment('/todos');
        break;
      case CreateViews.Tasks:
        path.segment('/tasks');
        break;
      case CreateViews.Records:
        path.segment('/all_work');
        break;
      case CreateViews.Initiatives:
        path.segment('/initiatives');
        break;
      case CreateViews.Releases:
        path.segment('/releases');
        break;
      case CreateViews.Features:
        path.segment('/features');
        break;
      case CreateViews.Ideas:
        path.segment('/ideas');
        break;
      case CreateViews.Recents:
        path.segment('/documents');
        path.segment('/recents');
        break;
      case CreateViews.Shared:
        path.segment('/documents');
        path.segment('/shared');
        break;
    }

    if (currentView === CreateViews.Document) {
      const internalPageParent =
        this.userPageTreeController?.state.pageHierarchy.internalPageParent(
          page?.id
        );

      if (internalPageParent) {
        const urlParams = setNestedPageId(page?.referenceNum || page?.id);

        path.segment(internalPageParent.referenceNum || internalPageParent.id);
        path.queryString(urlParams.toString());
      } else if (remoteDocument?.connectedPage?.id) {
        path.segment(`/shared/${remoteDocument.connectedPage.id}`);
      } else if (page?.id) {
        path.segment(page.referenceNum || page.id);
      }
    }
  }

  async actionDeletePage() {
    if (this.state.page) {
      const records = [this.state.page];
      const action = new DeleteRecordAction(records);
      action.runForRecords([this.state.page], {}, this);
    }
  }

  async actionShowSettings(_tab = null) {
    document.location.href = '/settings/profile';
  }

  async actionRestoreSidebarView() {
    this.state.pageLoading = true;
    await this.actionSetCurrentView(CreateViews.MyWork);
    this.state.pageLoading = false;
    this.state.settingsTab = null;
  }

  async shouldShowScreenSizeWarning() {
    const { toastMessageType } = this.state;

    // Early out: only show for desktop experience on small screens. Also,
    // prioritize other toast message types over screen size warning type
    if (
      !!toastMessageType ||
      !window.location.pathname.includes('notebooks') ||
      !isSmallScreen()
    ) {
      return false;
    }

    const lastDismissalTime = await UserPreference.select('value').findBy({
      name: 'user.pages.screenSizeWarningDismissalTime',
    });

    const time = parseInt(lastDismissalTime?.value, 10);
    const oneDayPassed =
      time && (Date.now() - time) / 1000 / (24 * 60 * 60) >= 1;

    // If time is NaN, then this is the first time we are showing this message
    return oneDayPassed || !time;
  }

  actionSetSelectedDate(date: Moment) {
    this.state.selectedDate = date;
    this.actionLoadPagesForSelectedDate();

    const now = moment();
    AhaAnalytics.track(
      'Feature-7177135300784649975',
      'User changes homepage date',
      {
        Date: now.isSame(date, 'day')
          ? 'Current'
          : now.isBefore(date, 'day')
          ? 'Future'
          : 'Past',
      }
    );
  }

  actionSetToday() {
    this.actionSetSelectedDate(moment().startOf('day'));
  }

  actionShowSearch(args: Partial<AskFredAnythingArgs> = {}) {
    askFredAnything({
      eventsOnly: true,
      ...args,
    } as any);

    AhaAnalytics.track('Feature-7177135300784649975', 'User opens search', {});
  }

  handleFredResultSelected = (
    event: AhaEventMap['aha.fred.result-selected']
  ) => {
    const result = event.detail.result;
    const { view_trackable_type, searchable_type, project_name, drawerable } =
      result;

    const type = searchable_type || view_trackable_type;

    if (type === 'Page') {
      if (!this.state.isRoadmaps || !project_name) {
        const reference = result.searchable_reference_num;
        this.actionNavigate(this.pageUrl(reference));

        // If we are in roadmaps and the result is a page from a
        // workspace open the Page in the drawer.
      } else if (this.state.isRoadmaps && project_name) {
        AhaDrawer.showUrl(result.url);
      }
    } else if (type === 'Task') {
      AhaDrawer.showUrl(result.url);
    } else if (this.state.isRoadmaps && drawerable) {
      // If we are in roadmaps and the result is drawerable, open the result in the drawer.
      AhaDrawer.showUrl(result.url);
    } else {
      Turbolinks.visit(result.url);
    }

    AhaAnalytics.track(
      'Feature-7177135300784649975',
      'User opens search result',
      {
        'Record type': type,
      }
    );
  };

  actionExportWhiteboard(type: 'png' | 'svg') {
    // Event listener in WhiteboardContainerController, which holds a ref to
    // the whiteboard component's internal controller.
    document.dispatchEvent(
      new CustomEvent('aha.whiteboard.export', { detail: { type } })
    );
  }

  actionApplyWhiteboardDependencyLinesTool(type: 'add' | 'remove' | 'refresh') {
    window.dispatchEvent(
      new CustomEvent('aha.whiteboard.applyDependencyLinesTool', {
        detail: { type },
      })
    );
  }

  actionToggleFullWidthEditor() {
    const fullWidthEditor = !this.state.fullWidthEditor;
    this.state.fullWidthEditor = fullWidthEditor;

    this.state.page.update({
      editorWidth: fullWidthEditor ? 'FULL' : 'NARROW',
    });
  }

  updateDocumentTitle(title: string) {
    const prefix = window.railsEnv === 'development' ? '[DEV] ' : '';
    if (title) {
      document.title = `${prefix}${title} | Aha!`;
    } else {
      document.title = `${prefix}Aha!`;
    }
  }

  /**
   * If we see a custom redirect event and its for a page then load the page.
   * This allows context actions to redirect via rails
   */
  handleRedirect = (event: AhaEventMap['aha.redirect_to']) => {
    const { detail } = event;

    if (detail.typename === 'Page' && detail.id) {
      event.preventDefault();
      this.actionLoadPageById(detail.id);
    }
  };

  handleSetProject = (event: AhaEventMap['aha.set_project']) => {
    this.actionSetProject(event.detail);
  };

  handleDestroy = (event: AhaEventMap['aha.destroy']) => {
    if (Page.is(event.detail)) {
      this.actionRemovePage(event.detail);
    }
  };

  shouldOpenProjectNotesInProject = (page: Page | ConnectedPage) => {
    const internalPageParent =
      this.userPageTreeController?.state.pageHierarchy.internalPageParent(
        page.id
      );

    return (
      this.state.isRoadmaps &&
      page.typename === 'Page' &&
      page.project &&
      !internalPageParent
    );
  };

  get canCreatePages() {
    if (this.state.project) {
      return this.state.project.canCreatePages;
    } else {
      return this.canCreatePersonalPages;
    }
  }

  trackPageView(path) {
    if (!AhaAnalytics.shouldTrack()) {
      return false;
    }

    return fetch(`/log_virtual_pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
      body: JSON.stringify({
        request: {
          fullpath: path,
        },
      }),
    });
  }

  actionUpdateCurrentPage(updates: {
    editLocked?: boolean;
    showContents?: boolean;
  }) {
    this.state.page.update(updates);
  }

  get hasRoadmapsOrIdeasOrTeamworkOrDevelopFlavor() {
    return (
      this.state.hasRoadmapsFlavor ||
      this.state.hasIdeasFlavor ||
      this.state.hasTeamworkOrDevelopFlavor
    );
  }
}

DesktopNotesController.extend(HasSelection);

export default DesktopNotesController;
